import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import Loading from "./Loading";
import { Spinner } from "./Spinner";

type props = {
  type: "info" | "danger" | "none";
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onConfirm: () => void;
  onCancel?: () => void;
  title: string;
  confirmButtonTitle?: string;
  description: string | JSX.Element;
  maxWidth?: string;
  /** Opt-in: caps the panel height and makes only the description body scroll, keeping the title and footer buttons always visible. Default layout (all other callers) is unchanged. */
  scrollBody?: boolean;
};

export default function ConfirmModal(props: props) {
  const cancelButtonRef = useRef(null);
  const danger = props.type === "danger";
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!props.open) {
      setIsLoading(false); 
    } else {
      setIsLoading(false); 
    }
  }, [props.open]);



  return (
    <Transition.Root show={props.open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={() => {
          props.setOpen(false);
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-400  bg-opacity-50 dark:bg-opacity-20 transition-opacity " />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className={`relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full ${props.maxWidth || 'sm:max-w-lg'} ${props.scrollBody ? "flex max-h-[85vh] flex-col" : ""}`}>
                {props.scrollBody ? (
                  <>
                    <div className="flex-shrink-0 bg-white px-4 pt-5 sm:px-6 sm:pt-6">
                      <div
                        className={`${props.type !== "none" ? "sm:flex sm:items-start" : ""
                          }`}
                      >
                        {props.type !== "none" && (
                          <div
                            className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${danger ? "bg-red-100" : "bg-blue-100"
                              } sm:mx-0 sm:h-10 sm:w-10`}
                          >
                            <ExclamationTriangleIcon
                              className={`h-6 w-6 ${danger ? "text-red-600" : "text-blue-600"
                                }`}
                              aria-hidden="true"
                            />
                          </div>
                        )}
                        <div className={`mt-3 text-center sm:mt-0 sm:text-left ${props.type !== "none" ? "sm:ml-4" : "w-full"}`}>
                          <Dialog.Title
                            as="h3"
                            className="text-base font-semibold leading-6 text-gray-900"
                          >
                            {props.title}
                          </Dialog.Title>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-2 sm:px-6 sm:pb-4 ${props.type !== "none" ? "sm:pl-14" : ""
                        }`}
                    >
                      {typeof props.description === "string" ? (
                        <p className="text-sm text-gray-500">
                          {props.description}
                        </p>
                      ) : (
                        props.description
                      )}
                    </div>
                  </>
                ) : (
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div
                      className={`${props.type !== "none" ? "sm:flex sm:items-start" : ""
                        }`}
                    >
                      {props.type !== "none" && (
                        <div
                          className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${danger ? "bg-red-100" : "bg-blue-100"
                            } sm:mx-0 sm:h-10 sm:w-10`}
                        >
                          <ExclamationTriangleIcon
                            className={`h-6 w-6 ${danger ? "text-red-600" : "text-blue-600"
                              }`}
                            aria-hidden="true"
                          />
                        </div>
                      )}
                      <div className={`mt-3 text-center sm:mt-0 sm:text-left ${props.type !== "none" ? "sm:ml-4" : "w-full"}`}>
                        <Dialog.Title
                          as="h3"
                          className="text-base font-semibold leading-6 text-gray-900"
                        >
                          {props.title}
                        </Dialog.Title>
                        <div className="mt-2">
                          {typeof props.description === "string" ? (
                            <p className="text-sm text-gray-500">
                              {props.description}
                            </p>
                          ) : (
                            props.description
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex-shrink-0 bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button
                    type="button"
                    className={`inline-flex w-full justify-center rounded-md ${danger ? "bg-red-600" : "bg-blue-600"
                      } px-3 py-2 text-sm font-semibold text-white shadow-sm ${danger ? "hover:bg-red-500" : "hover:bg-entntorange"
                      } sm:ml-3 sm:w-auto`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (!isLoading) {
                        setIsLoading(true);
                        try {
                          await Promise.resolve(props.onConfirm());
                        } catch (error) {
                          console.error("Error in onConfirm:", error);
                        } finally {
                          setIsLoading(false);
                        }
                      }
                    }}
                    disabled={isLoading}
                  >
                    {props.confirmButtonTitle
                      ? props.confirmButtonTitle
                      : isLoading ? <Spinner/> : "Confirm" }  
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={() => {
                      if (props.onCancel) props.onCancel();
                      props.setOpen(false);
                      setIsLoading(false);
                    }}
                    ref={cancelButtonRef}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
